import {
  Alert,
  Button,
  Card,
  CardContent,
  LinearProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { DatabaseStorageStateDto } from '../api/generated/client';
import { Box, Stack, Typography } from '../components/mui-system';

function bytes(value: number) {
  const units = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ'];
  const index = value > 0 ? Math.min(Math.floor(Math.log10(value) / 3), units.length - 1) : 0;
  return `${(value / 1000 ** index).toLocaleString('ru-RU', { maximumFractionDigits: index ? 2 : 0 })} ${units[index]}`;
}
function exactBytes(value: number) {
  return `${value.toLocaleString('ru-RU')} байт`;
}

export function DatabaseStoragePanel() {
  const [state, setState] = useState<DatabaseStorageStateDto | null>(null);
  const [requesting, setRequesting] = useState(true);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<'totalBytes' | 'tableBytes' | 'indexBytes'>('totalBytes');
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setRequesting(true);
    setError('');
    async function load(recalculate: boolean) {
      try {
        const value = await (recalculate ? api.refreshDatabaseStorage() : api.databaseStorage());
        if (!active) return;
        setState(value);
        setRequesting(false);
        setNow(Date.now());
        if (value.status === 'running') timer = setTimeout(() => void load(false), 1500);
      } catch (caught) {
        if (!active) return;
        setError(caught instanceof Error ? caught.message : 'Не удалось загрузить сводку базы.');
        setRequesting(false);
      }
    }
    void load(refresh > 0);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [refresh]);

  useEffect(() => {
    if (!state?.nextRefreshAt) return;
    const end = Date.parse(state.nextRefreshAt);
    if (end <= Date.now()) return;
    const timer = setInterval(() => {
      setNow(Date.now());
      if (Date.now() >= end) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [state?.nextRefreshAt]);

  const report = state?.report;
  const running = state?.status === 'running' && !error;
  const cooldown = Math.max(
    0,
    Math.ceil(((state?.nextRefreshAt ? Date.parse(state.nextRefreshAt) : 0) - now) / 1000),
  );
  const query = search.trim().toLocaleLowerCase('ru-RU');
  const tables =
    report?.tables
      .filter((table) =>
        `${table.name} ${table.label} ${table.group}`.toLocaleLowerCase('ru-RU').includes(query),
      )
      .sort((a, b) => b[sort] - a[sort] || a.name.localeCompare(b.name)) ?? [];

  return (
    <Stack gap={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} justifyContent="space-between">
        <Box>
          <Typography variant="h3">База данных</Typography>
          <Typography color="text.secondary" mt={0.5}>
            Общая база всех воркспейсов. Размер таблиц, индексов и служебных файлов.
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            disabled={requesting || running || cooldown > 0}
            onClick={() => setRefresh((value) => value + 1)}
          >
            Пересчитать
          </Button>
          {cooldown > 0 && !running && (
            <Typography color="text.secondary" fontSize={12} mt={0.5}>
              Повторный замер через {cooldown} с
            </Typography>
          )}
        </Box>
      </Stack>
      {(requesting || running) && (
        <Box>
          <LinearProgress aria-label="Измерение размера базы" />
          <Typography color="text.secondary" fontSize={13} mt={1}>
            Измеряем объём. На большой базе это может занять до минуты; можно уйти с этой страницы.
          </Typography>
        </Box>
      )}
      {(error || state?.error) && <Alert severity="error">{error || state?.error}</Alert>}
      {report && (
        <>
          <Typography color="text.secondary" fontSize={13}>
            {running || error || state?.status === 'error' ? 'Последний успешный замер' : 'Замер'}:{' '}
            {new Date(report.measuredAt).toLocaleString('ru-RU')} ·{' '}
            {(report.durationMs / 1000).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} с
          </Typography>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }}
            gap={2}
          >
            {[
              ['Файлы базы', report.totalFileBytes, 'Основной файл + WAL + SHM'],
              [
                'Таблицы и индексы',
                report.tableBytes + report.indexBytes,
                'Включая служебные данные и историю',
              ],
              ['Свободно внутри БД', report.freeBytes, 'Может использоваться следующими записями'],
              ['Журнал WAL', report.walFileBytes, 'Записи до переноса в основной файл'],
            ].map(([label, value, hint]) => (
              <Card key={label as string} variant="outlined">
                <CardContent>
                  <Typography color="text.secondary" fontSize={13}>
                    {label}
                  </Typography>
                  <Typography variant="h3" my={1} title={exactBytes(value as number)}>
                    {bytes(value as number)}
                  </Typography>
                  <Typography color="text.secondary" fontSize={12}>
                    {hint}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
          <Typography color="text.secondary" fontSize={13}>
            Основной файл: {bytes(report.databaseFileBytes)} · SHM: {bytes(report.shmFileBytes)} ·{' '}
            Служебные страницы вне таблиц: {bytes(report.otherBytes)}
          </Typography>
          <Card variant="outlined">
            <CardContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} mb={2}>
                <TextField
                  label="Найти таблицу или раздел"
                  size="small"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  sx={{ flex: 1 }}
                />
                <TextField
                  select
                  label="Сначала самые большие"
                  size="small"
                  value={sort}
                  onChange={(event) => setSort(event.target.value as typeof sort)}
                  sx={{ minWidth: 220 }}
                >
                  <MenuItem value="totalBytes">По общему объёму</MenuItem>
                  <MenuItem value="tableBytes">По объёму таблицы</MenuItem>
                  <MenuItem value="indexBytes">По объёму индексов</MenuItem>
                </TextField>
              </Stack>
              <TableContainer sx={{ maxHeight: 640 }}>
                <Table size="small" stickyHeader aria-label="Объём таблиц базы данных">
                  <TableHead>
                    <TableRow>
                      <TableCell>Данные</TableCell>
                      <TableCell>Раздел</TableCell>
                      <TableCell align="right">Таблица</TableCell>
                      <TableCell align="right">Индексы</TableCell>
                      <TableCell align="right">Всего</TableCell>
                      <TableCell align="right">Доля БД</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tables.map((table) => (
                      <TableRow key={table.name}>
                        <TableCell sx={{ minWidth: 220 }}>
                          <Typography fontSize={13}>{table.label}</Typography>
                          {table.label !== table.name && (
                            <Typography
                              fontSize={11}
                              color="text.secondary"
                              sx={{ overflowWrap: 'anywhere' }}
                            >
                              {table.name}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{table.group}</TableCell>
                        {[table.tableBytes, table.indexBytes, table.totalBytes].map(
                          (value, index) => (
                            <TableCell
                              key={index}
                              align="right"
                              title={exactBytes(value)}
                              sx={{
                                whiteSpace: 'nowrap',
                                fontVariantNumeric: 'tabular-nums',
                                fontWeight: index === 2 ? 700 : undefined,
                              }}
                            >
                              {bytes(value)}
                            </TableCell>
                          ),
                        )}
                        <TableCell align="right">
                          {(report.databaseBytes
                            ? (table.totalBytes / report.databaseBytes) * 100
                            : 0
                          ).toLocaleString('ru-RU', { maximumFractionDigits: 2 })}
                          %
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {!tables.length && (
                <Typography color="text.secondary" py={3}>
                  Нет таблиц для выбранного поиска.
                </Typography>
              )}
              <Typography color="text.secondary" fontSize={12} mt={2}>
                Показано {tables.length} из {report.tables.length} таблиц. Доля рассчитана от
                логического размера БД: {bytes(report.databaseBytes)}.
              </Typography>
            </CardContent>
          </Card>
          <Alert severity="info">
            В сводке только основная SQLite-база. Файлы сайтов, медиа, резервные копии и отдельные
            архивные базы Telegram Studio сюда не входят. Размер файла и логический размер могут
            различаться во время записи.
          </Alert>
        </>
      )}
    </Stack>
  );
}
